/**
 * 지형 그리드. 타일 하나에 지형 종류 하나가 들어간다.
 * 흐름장(flow field)과 유닛 이동과 피해 판정이 공유하는 유일한 지형 표현이다.
 *
 * 지형은 **이미 있는 규칙에 손을 얹는 방식**으로만 붙인다. 새 규칙을 만들면
 * 배워야 할 것이 늘고, 그만큼 화면에서 읽히지 않는 일이 생긴다. 숲은 기병의
 * 돌격을, 늪은 대열을, 언덕은 높은 데서 치는 쪽을 건드린다. 셋 다 플레이어가
 * 이미 알고 있는 것들이다.
 */
import { FP_ONE, assertWorldBounds } from './fixed.ts';
import { BranchCode } from './troops.ts';
import type { Rng } from './rng.ts';

export const TILE_PX = 32;
export const TILE_FP = TILE_PX * FP_ONE;

export const Cell = {
  Open: 0,
  Blocked: 1,
  /** 숲. 느리고, 기병은 더 느리다. 돌격이 붙지 않고 화살이 가려진다. */
  Forest: 2,
  /** 늪. 누구에게나 느리다. 대열이 여기서 끊어진다. */
  Marsh: 3,
  /** 언덕. 오르기 조금 힘들고, 위에서 치면 더 아프다. */
  Hill: 4,
  /**
   * 여울(전장 특성 `Trait.Ford`). 얕은 물줄기가 전장을 가로지른다. 느리고, 물에 선 사람은 방어가 깎이고
   * (`FORD_DEF_MALUS`), 돌격이 붙지 않는다. 먼저 건너는 쪽이 불리하다.
   */
  Ford: 5,
  /**
   * 거마(拒馬, 군수품 `Supply.Stakes`·전장 특성 `Trait.Siege`). 엇갈려 박은 말뚝 — 사람은 사이로 지나가고 말만 크게
   * 느리다(`HORSE_MOVE_PCT`). 돌격이 붙지 않는다(`roughGround`). 막힌 칸이 아니라 화살이 넘어가고 흐름장도 사람 걸음으로
   * 잰다. 처음 설계(목책)는 말뚝을 바위로 세워 우리 대형까지 막았다(supplies.ts 머리말).
   */
  Stakes: 6,
} as const;
export type Cell = (typeof Cell)[keyof typeof Cell];

export const CELL_NAMES: Record<Cell, string> = {
  [Cell.Open]: '평지',
  [Cell.Blocked]: '바위',
  [Cell.Forest]: '숲',
  [Cell.Marsh]: '늪',
  [Cell.Hill]: '언덕',
  [Cell.Ford]: '여울',
  [Cell.Stakes]: '거마',
};

/**
 * 지형별 이동 속도(백분율).
 *
 * 막힌 칸은 들어가지 않으므로 값이 의미 없지만, 표를 빠짐없이 채워 두면
 * 지형을 하나 더할 때 여기를 빼먹고 지나갈 수 없다.
 *
 * 처음에는 숲 70 · 늪 55 · 언덕 85로 잡았다가 올렸다. 그 값에서는 깊이 3의
 * 장수 생존이 12/15에서 8/15로, 전과 중앙값이 1842에서 563으로 떨어졌다.
 * 느려지는 것 자체가 아니라 **대열이 끊겨 장수가 고립되는 것**이 문제였다.
 * 지형이 하는 일(기병을 세우고, 높은 자리를 값지게 하고, 화살을 가리는 것)은
 * 속도를 절반으로 깎지 않아도 성립한다.
 */
export const MOVE_PCT: Record<Cell, number> = {
  [Cell.Open]: 100,
  [Cell.Blocked]: 100,
  [Cell.Forest]: 80,
  [Cell.Marsh]: 70,
  [Cell.Hill]: 90,
  [Cell.Ford]: 75,
  [Cell.Stakes]: 100,
};

/**
 * 여울에 선 사람의 방어 감산. 물살에 발이 묶여 막아낼 손이 굼뜨다. 속도만 깎으면 늪과 다를 것이 없어, 건너는 동안
 * 맞는 값을 따로 둔다 — "먼저 건너는 쪽이 불리하다"가 여기서 나온다.
 */
export const FORD_DEF_MALUS = 1;

/** 여울의 폭(칸). 전장 한가운데를 가로로 가로지른다. */
export const FORD_ROWS = 2;

/**
 * 기병만 따로 받는 값. 숲에서 말은 사람보다 훨씬 못 간다.
 *
 * 이것이 숲의 존재 이유다. 상성표에서 기병을 세우는 것은 창뿐인데, 창을
 * 준비하지 못한 편성에게도 대응할 자리를 준다. 지형이 병종 하나를 통째로
 * 무력화하면 안 되므로 늪과 언덕에는 기병 전용 보정을 두지 않았다.
 */
export const HORSE_MOVE_PCT: Partial<Record<Cell, number>> = {
  [Cell.Forest]: 60,
  // 거마: 말은 말뚝 사이를 거의 못 지난다. 막지는 않는다 — 막으면 흐름장이 기병만 따로 길을 찾아야 한다.
  [Cell.Stakes]: 30,
};

export function movePct(cell: Cell, branch: BranchCode): number {
  if (branch === BranchCode.Horse) {
    const special = HORSE_MOVE_PCT[cell];
    if (special !== undefined) return special;
  }
  return MOVE_PCT[cell];
}

export class TerrainGrid {
  readonly cols: number;
  readonly rows: number;
  readonly widthPx: number;
  readonly heightPx: number;
  readonly cells: Uint8Array;

  constructor(cols: number, rows: number) {
    this.cols = cols;
    this.rows = rows;
    this.widthPx = cols * TILE_PX;
    this.heightPx = rows * TILE_PX;
    assertWorldBounds(this.widthPx, this.heightPx);
    this.cells = new Uint8Array(cols * rows);
  }

  idx(cx: number, cy: number): number {
    return cy * this.cols + cx;
  }

  inBounds(cx: number, cy: number): boolean {
    return cx >= 0 && cy >= 0 && cx < this.cols && cy < this.rows;
  }

  isBlocked(cx: number, cy: number): boolean {
    if (!this.inBounds(cx, cy)) return true;
    return this.cells[cy * this.cols + cx] === Cell.Blocked;
  }

  /** 이 칸의 지형. 밖이면 바위로 친다. */
  cellAt(cx: number, cy: number): Cell {
    if (!this.inBounds(cx, cy)) return Cell.Blocked;
    return this.cells[cy * this.cols + cx] as Cell;
  }

  /** 고정소수점 좌표가 선 칸의 지형. 전투 판정이 매 타격 부른다. */
  cellAtFp(x: number, y: number): Cell {
    return this.cellAt(Math.floor(x / TILE_FP), Math.floor(y / TILE_FP));
  }

  /**
   * 두 점 사이에 바위가 있는가. 화살이 지나갈 수 있는지 보는 데 쓴다.
   *
   * **지나는 칸을 하나도 빠뜨리지 않는다.** 처음에는 긴 축의 칸 수만큼 잘라
   * 점을 찍었는데, 그러면 대각으로 맞닿은 바위 둘 사이를 그냥 통과한다 —
   * 점이 모서리를 건너뛰기 때문이다. 6만 쌍을 촘촘히 비교해 보니 1.25%가
   * "뚫렸다"고 잘못 답했다. 한쪽으로만 새는 오차라 눈에 띄지 않는다.
   *
   * 그래서 칸 경계를 하나씩 넘는다. x로 갈지 y로 갈지는 다음 경계까지의
   * 거리를 분수 비교로 정한다 — 나눗셈 없이 정수 곱으로만 본다. 실수가
   * 섞이면 서버가 같은 답을 못 낸다.
   */
  blockedBetweenFp(x0: number, y0: number, x1: number, y1: number): boolean {
    let cx = Math.floor(x0 / TILE_FP);
    let cy = Math.floor(y0 / TILE_FP);
    const cx1 = Math.floor(x1 / TILE_FP);
    const cy1 = Math.floor(y1 / TILE_FP);
    if (cx === cx1 && cy === cy1) return false;

    const dx = x1 - x0;
    const dy = y1 - y0;
    const sx = dx > 0 ? 1 : -1;
    const sy = dy > 0 ? 1 : -1;
    const adx = Math.abs(dx);
    const ady = Math.abs(dy);

    // 다음 세로/가로 경계까지 남은 거리.
    let tx = dx > 0 ? (cx + 1) * TILE_FP - x0 : x0 - cx * TILE_FP;
    let ty = dy > 0 ? (cy + 1) * TILE_FP - y0 : y0 - cy * TILE_FP;

    // 칸 수만큼만 돈다. 어떤 직선도 이보다 많은 칸을 지나지 않는다.
    const limit = Math.abs(cx1 - cx) + Math.abs(cy1 - cy) + 2;
    for (let n = 0; n < limit; n++) {
      // tx/adx 와 ty/ady 를 나눗셈 없이 견준다. 축 하나가 0이면 그쪽은 영원히 뒤다.
      const takeX = ady === 0 || (adx !== 0 && tx * ady <= ty * adx);
      if (takeX) {
        cx += sx;
        tx += TILE_FP;
      } else {
        cy += sy;
        ty += TILE_FP;
      }
      // 끝 칸은 보지 않는다. 쏘는 쪽과 맞는 쪽이 선 칸은 이미 지나갈 수 있다.
      if (cx === cx1 && cy === cy1) return false;
      if (this.isBlocked(cx, cy)) return true;
    }
    return false;
  }

  /** 고정소수점 좌표가 벽 안인지. */
  isBlockedFp(x: number, y: number): boolean {
    return this.isBlocked(Math.floor(x / TILE_FP), Math.floor(y / TILE_FP));
  }

  cellOfFp(x: number, y: number, out: Int32Array): void {
    out[0] = Math.floor(x / TILE_FP);
    out[1] = Math.floor(y / TILE_FP);
  }

  /**
   * 시드 기반 지형 생성.
   *
   * 좌우 테두리는 벽이고(위아래는 열려 있다), 안쪽에 바위·숲·늪·언덕을 흩는다. **막힌 칸만 중앙
   * 회랑에서 뺀다.** 나머지 셋은 지나갈 수 있으므로 양군이 만나는 데 지장이
   * 없고, 오히려 싸움이 벌어지는 자리에 있어야 값을 한다. 회랑 밖에만 두면
   * 아무도 밟지 않는 장식이 된다.
   *
   * @param land 전장 특성(traits.ts)이 그 지형 덩어리 수에 곱하는 배수. 없으면 1 — 특성이 없는 판은 난수 소비까지
   * 예전과 같다. `rocks`·`forest`가 0이면(넓은 평원) 그 덩어리를 깔지 않는다. 난수는 그대로 굴린다 — 뒤따르는
   * 지형의 자리가 특성 없는 판과 같은 흐름에서 나오게.
   */
  static generate(
    cols: number,
    rows: number,
    rng: Rng,
    land?: { forest?: number; marsh?: number; rocks?: number },
  ): TerrainGrid {
    const g = new TerrainGrid(cols, rows);
    // **위아래는 열어 둔다.** 양군이 그쪽에서 들어왔고, 후퇴하는 병력이 그쪽으로
    // 빠져나간다. 예전에는 네 변을 다 바위로 둘렀는데, 그러면 무너져 달아나는
    // 병사가 벽에 붙어 잡혀 죽을 때까지 서 있었다. 좌우만 벽이다.
    for (let y = 0; y < rows; y++) {
      g.cells[g.idx(0, y)] = Cell.Blocked;
      g.cells[g.idx(cols - 1, y)] = Cell.Blocked;
    }

    // 맵 넓이에 비례해서 흩는다. 고정 개수로 두면 작은 맵이 바위밭이 된다.
    const clumps = Math.max(4, Math.floor((cols * rows) / 130)) + rng.int(5);
    const midY0 = Math.floor(rows * 0.35);
    const midY1 = Math.floor(rows * 0.65);
    for (let i = 0; i < clumps; i++) {
      const cx = rng.range(2, cols - 3);
      const cy = rng.range(2, rows - 3);
      const r = rng.range(1, 2);
      // 중앙 회랑은 비워둔다. **덩어리 전체**를 보고 판단해야 한다. 가운데만
      // 보면 회랑 바로 바깥에 찍힌 바위가 반지름만큼 회랑 안으로 넘어온다.
      // 검산 도구를 만들고 나서야 회랑의 5%가 막혀 있는 것을 알았다.
      if (cy + r >= midY0 && cy - r <= midY1) continue;
      if (land?.rocks === 0) continue;
      blob(g, cx, cy, r, Cell.Blocked);
    }

    // 지나갈 수 있는 지형은 회랑 안에도 둔다. 밟히지 않으면 값을 못 한다.
    //
    // 숲이 가장 적다. 처음에는 언덕과 비슷하게 깔았는데, 그러면 우회가
    // 정면보다 31% 손해가 됐다. `npm run tactic`이 그 경고를 직접 찍었다.
    // 원인을 네 조건으로 갈라 재 보니 기병의 숲 벌점이 아니라 **숲의 양**이
    // 문제였다(벌점을 아예 빼면 오히려 −54%로 더 나빠졌다). 숲이 많으면
    // 돌아가는 길이 통째로 막혀 우회 자체가 성립하지 않는다. 절반으로 줄이니
    // +25%가 됐고, 이는 지형이 아예 없을 때(+7%)보다도 낫다. 숲은 길을
    // 막는 것이 아니라 길을 고르게 만드는 정도로만 있어야 한다.
    const patches: [Cell, number, number][] = [
      [Cell.Hill, Math.max(2, Math.floor((cols * rows) / 300)), 3],
      // 숲을 빼는 판(`forest: 0`)도 덩어리 수는 그대로 세어 난수를 같게 굴리고, 깔기만 건너뛴다 — 처음에는 수를 0으로
      // 만들어 뒤따르는 늪의 자리가 다른 수열에서 나왔다(리뷰가 짚었다).
      [Cell.Forest, Math.max(1, Math.floor((cols * rows) / 700)) * (land?.forest === 0 ? 1 : (land?.forest ?? 1)), 2],
      [Cell.Marsh, Math.max(1, Math.floor((cols * rows) / 600)) * (land?.marsh ?? 1), 2],
    ];
    for (const [kind, count, maxR] of patches) {
      // 난수는 루프 밖에서 한 번만 굴린다. 조건식에 두면 반복마다 굴려,
      // 덩어리 수가 흔들리고 뒤따르는 지형의 자리까지 통째로 밀린다.
      const n = count + rng.int(2);
      for (let i = 0; i < n; i++) {
        const cx = rng.range(3, cols - 4);
        const cy = rng.range(3, rows - 4);
        const r = rng.range(1, maxR);
        if (kind === Cell.Forest && land?.forest === 0) continue;
        blob(g, cx, cy, r, kind);
      }
    }

    sealPockets(g);
    return g;
  }
}

/**
 * 여울을 깐다(`Trait.Ford`). 전장 한가운데를 가로로 `FORD_ROWS`칸, 벽을 뺀 모든 칸 — 끊긴 곳이 있으면 모두 그
 * 틈으로만 건너 여울이 길목 하나로 줄어든다. 바위는 덮지 않는다(중앙 회랑에는 원래 바위가 없다).
 */
export function layFord(g: TerrainGrid): void {
  const y0 = Math.floor(g.rows / 2) - Math.floor(FORD_ROWS / 2);
  for (let cy = y0; cy < y0 + FORD_ROWS; cy++) {
    for (let cx = 1; cx < g.cols - 1; cx++) {
      if (g.cells[g.idx(cx, cy)] !== Cell.Blocked) g.cells[g.idx(cx, cy)] = Cell.Ford;
    }
  }
}

/**
 * 성벽 한 줄을 세운다(전장 특성 `Trait.Fortress`). 줄 `cy`의 벽 안쪽 칸을 모두 바위로, `gates`(칸 x, 가운데)마다 `gateW`칸 폭의
 * 문을 비운다. 문이 좁아야 성문 공방이다 — 몰려 들어가는 곳에서 싸움이 난다. 세운 뒤 갇힌 곳을 메운다(`sealPockets`) —
 * 문이 있으니 성 안은 이어져 있다.
 *
 * 문 앞뒤(`GATE_APPROACH`줄씩)도 비운다. 벽 줄만 열었더니 들판의 바위가 문 바로 위에 걸린 시드에서는 그 문이 막힌 채
 * 메워져, 세 문 가운데 하나만 통로인 판이 천에 백 남짓 나왔다(리뷰가 짚었다).
 */
export function raiseWall(g: TerrainGrid, cy: number, gates: readonly number[], gateW: number): void {
  if (cy < 0 || cy >= g.rows) return;
  for (let cx = 1; cx < g.cols - 1; cx++) {
    const inGate = gates.some((x) => cx >= x - (gateW >> 1) && cx < x - (gateW >> 1) + gateW);
    g.cells[g.idx(cx, cy)] = inGate ? Cell.Open : Cell.Blocked;
    if (!inGate) continue;
    for (let d = 1; d <= GATE_APPROACH; d++) {
      for (const y of [cy - d, cy + d]) {
        if (y > 0 && y < g.rows - 1 && g.cells[g.idx(cx, y)] === Cell.Blocked) g.cells[g.idx(cx, y)] = Cell.Open;
      }
    }
  }
  sealPockets(g);
}

/** 성문 앞뒤로 비우는 줄 수(`raiseWall`). */
export const GATE_APPROACH = 2;

/**
 * 거마 줄을 깐다(군수품 `Supply.Stakes`, 전장 특성 `Trait.Siege`). 칸 좌표 `x0`~`x1`, 줄 `rows`. 사람은 지나가므로 틈을 두지
 * 않는다 — 틈을 두면 말이 그 틈으로만 달려 든다. 바위는 덮지 않는다.
 */
export function plantStakes(g: TerrainGrid, x0: number, x1: number, rows: readonly number[]): void {
  for (const cy of rows) {
    if (cy < 0 || cy >= g.rows) continue;
    for (let cx = Math.max(1, x0); cx <= Math.min(g.cols - 2, x1); cx++) {
      const i = g.idx(cx, cy);
      if (g.cells[i] !== Cell.Blocked) g.cells[i] = Cell.Stakes;
    }
  }
}

/**
 * 언덕 한 판을 깐다(`Trait.HighGround` — 적이 서는 자리). 칸 좌표 사각형, 바위는 덮지 않는다. 위에서 치는 값
 * (`HILL_PCT`)은 언덕의 규칙 그대로다.
 */
export function raiseHill(g: TerrainGrid, x0: number, y0: number, x1: number, y1: number): void {
  for (let cy = Math.max(0, y0); cy <= Math.min(g.rows - 1, y1); cy++) {
    for (let cx = Math.max(1, x0); cx <= Math.min(g.cols - 2, x1); cx++) {
      if (g.cells[g.idx(cx, cy)] !== Cell.Blocked) g.cells[g.idx(cx, cy)] = Cell.Hill;
    }
  }
}

/**
 * 어디서도 닿지 못하는 칸을 바위로 메운다.
 *
 * 바위 덩어리들이 우연히 고리를 이루면 그 안쪽이 통째로 갇힌다. 거기 선 적은
 * 나오지도 못하고 아군이 들어가지도 못해 **전투가 끝나지 않는다.** 깊이 3을
 * 열다섯 시드로 돌려 보니, 시간 초과한 판에 남은 적 143명 중 46명이 그런
 * 자리에 있었다(7 시드). 플레이로만 드러나는 종류의 버그다 — 검산 도구는
 * 중앙 회랑만 보고 연결성은 보지 않았다.
 *
 * 메우는 쪽을 골랐다. 틈을 뚫으면 바위의 모양이 시드마다 달라지는 것을 넘어
 * 회랑 규칙까지 다시 봐야 하는데, 갇힌 자리는 어차피 아무도 못 쓰는 땅이다.
 * 가장 큰 구역만 남기고 나머지를 바위로 바꾸면 규칙이 하나로 끝난다.
 *
 * 네 방향으로만 잇는다. 유닛은 대각선으로 바위 사이를 빠져나가지 못한다.
 */
export function sealPockets(g: TerrainGrid): void {
  const n = g.cols * g.rows;
  const region = new Int32Array(n).fill(-1);
  const stack: number[] = [];
  let best = -1;
  let bestSize = 0;
  let count = 0;

  for (let start = 0; start < n; start++) {
    if (g.cells[start] === Cell.Blocked || region[start] >= 0) continue;
    const id = count++;
    let size = 0;
    region[start] = id;
    stack.push(start);
    while (stack.length > 0) {
      const c = stack.pop()!;
      size++;
      const cx = c % g.cols;
      const cy = (c - cx) / g.cols;
      if (cx > 0) pushOpen(g, region, stack, c - 1, id);
      if (cx < g.cols - 1) pushOpen(g, region, stack, c + 1, id);
      if (cy > 0) pushOpen(g, region, stack, c - g.cols, id);
      if (cy < g.rows - 1) pushOpen(g, region, stack, c + g.cols, id);
    }
    if (size > bestSize) {
      bestSize = size;
      best = id;
    }
  }

  if (best < 0) return;
  for (let i = 0; i < n; i++) {
    if (region[i] >= 0 && region[i] !== best) g.cells[i] = Cell.Blocked;
  }
}

/**
 * 열린 칸을 네 방향으로 묶어 구역 크기를 돌려준다. 유닛은 대각선으로 바위
 * 사이를 빠져나가지 못하므로 네 방향이 맞다.
 *
 * 도구와 시험이 저마다 복사해 두고 있었다. 세 벌이 조금씩 달라지면 어느
 * 것이 맞는지 알 수 없다.
 */
export function regionSizes(g: TerrainGrid): number[] {
  const n = g.cols * g.rows;
  const seen = new Uint8Array(n);
  const out: number[] = [];
  const stack: number[] = [];
  for (let start = 0; start < n; start++) {
    if (g.cells[start] === Cell.Blocked || seen[start]) continue;
    let size = 0;
    seen[start] = 1;
    stack.push(start);
    while (stack.length > 0) {
      const c = stack.pop()!;
      size++;
      const cx = c % g.cols;
      const cy = (c - cx) / g.cols;
      if (cx > 0) visit(g, seen, stack, c - 1);
      if (cx < g.cols - 1) visit(g, seen, stack, c + 1);
      if (cy > 0) visit(g, seen, stack, c - g.cols);
      if (cy < g.rows - 1) visit(g, seen, stack, c + g.cols);
    }
    out.push(size);
  }
  return out;
}

function visit(g: TerrainGrid, seen: Uint8Array, stack: number[], i: number): void {
  if (g.cells[i] === Cell.Blocked || seen[i]) return;
  seen[i] = 1;
  stack.push(i);
}

function pushOpen(
  g: TerrainGrid,
  region: Int32Array,
  stack: number[],
  i: number,
  id: number,
): void {
  if (g.cells[i] === Cell.Blocked || region[i] >= 0) return;
  region[i] = id;
  stack.push(i);
}

/** 원형 덩어리 하나. 바위 위에는 덮어쓰지 않는다. 길을 다시 뚫으면 안 된다. */
function blob(g: TerrainGrid, cx: number, cy: number, r: number, kind: Cell): void {
  for (let dy = -r; dy <= r; dy++) {
    for (let dx = -r; dx <= r; dx++) {
      if (dx * dx + dy * dy > r * r) continue;
      const nx = cx + dx;
      const ny = cy + dy;
      if (!g.inBounds(nx, ny)) continue;
      if (kind !== Cell.Blocked && g.cells[g.idx(nx, ny)] === Cell.Blocked) continue;
      g.cells[g.idx(nx, ny)] = kind;
    }
  }
}
